import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Banner } from "../../components/Controls";
import { CodeEntry } from "../../components/CodeEntry";
import { audit } from "../../lib/api";
import { ROLE_OPTIONS, SECTOR_OPTIONS, COUNTRY_OPTIONS } from "../../data/questions";

const emptyForm = {
  full_name: "",
  work_email: "",
  organisation: "",
  role: "",
  role_other: "",
  sector: "",
  sector_other: "",
  country: "",
  phone: "",
};

export default function Plan2Gate() {
  const nav = useNavigate();
  const [step, setStep] = useState("gate");
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [banner, setBanner] = useState(null);
  const [devCode, setDevCode] = useState(null);
  const [codeError, setCodeError] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [inProgress, setInProgress] = useState(false);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const roleFinal = () => (form.role === "Other" && form.role_other ? form.role_other : form.role);
  const sectorFinal = () => (form.sector === "Other" && form.sector_other ? form.sector_other : form.sector);

  const validate = () => {
    const errs = {};
    ["full_name", "work_email", "organisation", "role", "sector", "country"].forEach((k) => {
      if (!form[k]) errs[k] = "This field is required.";
    });
    if (form.role === "Other" && !form.role_other) errs.role_other = "Please tell us your role.";
    if (form.sector === "Other" && !form.sector_other) errs.sector_other = "Please tell us your sector.";
    if (form.work_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.work_email)) {
      errs.work_email = "Please check the email address.";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submitGate = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setBanner(null);
    try {
      const payload = {
        full_name: form.full_name,
        work_email: form.work_email,
        organisation: form.organisation,
        role: roleFinal(),
        sector: sectorFinal(),
        country: form.country,
        phone: form.phone || null,
        consent: true,
        theme: "apple",
      };
      const res = await audit.gate(payload);
      setDevCode(res.dev_code || null);
      setInProgress(!!res.in_progress);
      setResendCooldown(30);
      setStep("verify");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      if (String(detail).toLowerCase().includes("organisation")) {
        setErrors((e2) => ({ ...e2, work_email: detail }));
      } else {
        setBanner({ kind: "error", text: detail });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submitCode = async (code) => {
    setCodeError(null);
    try {
      const res = await audit.verify({ email: form.work_email.toLowerCase(), code });
      localStorage.setItem("cw_audit_token", res.token);
      nav(`/plan-2/questionnaire?token=${encodeURIComponent(res.token)}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || "That code is not correct.";
      setCodeError(detail);
    }
  };

  const resend = async () => {
    if (resendCooldown > 0) return;
    try {
      const payload = {
        full_name: form.full_name,
        work_email: form.work_email,
        organisation: form.organisation,
        role: roleFinal(),
        sector: sectorFinal(),
        country: form.country,
        phone: form.phone || null,
        consent: true,
        theme: "apple",
      };
      const res = await audit.gate(payload);
      setDevCode(res.dev_code || null);
      setResendCooldown(30);
      setCodeError(null);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not resend the code.";
      setBanner({ kind: "error", text: detail });
    }
  };

  const resumeSend = async () => {
    try {
      await audit.resumeRequest(form.work_email);
      setBanner({ kind: "info", text: "We have emailed you a link to continue where you left off." });
    } catch { /* silent */ }
  };

  return (
    <div className="p2-assessment cw-assessment cw-flow" data-testid="gate-page">
      <div className="cw-flow-column">
        {step === "gate" && (
          <div className="cw-fade-in">
            <button
              className="cw-btn-text"
              onClick={() => nav("/")}
              data-testid="gate-back-btn"
              style={{ marginBottom: 24 }}
            >
              &larr; Back to home
            </button>
            <div className="cw-eyebrow" data-testid="gate-eyebrow">Crowther AI Strategy Audit</div>
            <hr className="cw-rule" style={{ marginTop: 20 }} />
            <h1 className="cw-display-l" data-testid="gate-heading">
              Where should we send your report?
            </h1>
            <p className="cw-body" style={{ marginTop: 16, color: "rgba(22,30,27,0.72)" }} data-testid="gate-subline">
              The Crowther AI Strategy Audit is prepared from your answers and delivered to your work
              email within the hour.
            </p>

            {inProgress && (
              <div style={{ marginTop: 24 }}>
                <Banner kind="info" testid="in-progress-banner">
                  You have an audit in progress.{" "}
                  <button className="cw-btn-text" onClick={resumeSend} data-testid="resume-send-link" style={{ marginLeft: 8 }}>
                    Send my resume link
                  </button>
                </Banner>
              </div>
            )}

            {banner && (
              <div style={{ marginTop: 24 }}>
                <Banner kind={banner.kind}>{banner.text}</Banner>
              </div>
            )}

            <form onSubmit={submitGate} style={{ marginTop: 40, display: "grid", gap: 20 }} data-testid="gate-form">
              <Field label="Full name" name="full_name" placeholder="Adaeze Okonkwo" value={form.full_name} onChange={setField("full_name")} error={errors.full_name} />
              <Field label="Work email" name="work_email" type="email" placeholder="you@yourorganisation.com" value={form.work_email} onChange={setField("work_email")} error={errors.work_email} />
              <Field label="Organisation name" name="organisation" placeholder="Your organisation" value={form.organisation} onChange={setField("organisation")} error={errors.organisation} />

              <div>
                <SelectField
                  label="Your role"
                  name="role"
                  value={form.role}
                  onChange={setField("role")}
                  error={errors.role}
                  options={ROLE_OPTIONS}
                  placeholder="Select your role"
                />
                {form.role === "Other" && (
                  <div style={{ marginTop: 12 }} data-testid="role-other-wrap">
                    <Field
                      label="Please tell us your role"
                      name="role_other"
                      placeholder="e.g. Head of Data Science"
                      value={form.role_other}
                      onChange={setField("role_other")}
                      error={errors.role_other}
                    />
                  </div>
                )}
              </div>

              <div>
                <SelectField
                  label="Sector"
                  name="sector"
                  value={form.sector}
                  onChange={setField("sector")}
                  error={errors.sector}
                  options={SECTOR_OPTIONS}
                  placeholder="Select your sector"
                />
                {form.sector === "Other" && (
                  <div style={{ marginTop: 12 }} data-testid="sector-other-wrap">
                    <Field
                      label="Please tell us your sector"
                      name="sector_other"
                      placeholder="e.g. Logistics"
                      value={form.sector_other}
                      onChange={setField("sector_other")}
                      error={errors.sector_other}
                    />
                  </div>
                )}
              </div>

              <SelectField
                label="Country of primary operation"
                name="country"
                value={form.country}
                onChange={setField("country")}
                error={errors.country}
                options={COUNTRY_OPTIONS.map(([code, name]) => ({ value: code, label: name }))}
                placeholder="Select country"
              />

              <div data-testid="field-phone">
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, marginBottom: 8, flexWrap: "wrap" }}>
                  <div className="cw-label" style={{ marginBottom: 0 }}>Phone number</div>
                  <div className="cw-small" style={{ color: "rgba(22,30,27,0.55)" }}>
                    Optional. If you would like a walkthrough of your results, we will call to arrange it.
                  </div>
                </div>
                <input
                  type="tel"
                  className="cw-input"
                  name="phone"
                  placeholder="+234 801 234 5678"
                  value={form.phone}
                  onChange={setField("phone")}
                  data-testid="input-phone"
                />
              </div>

              <p className="cw-small" style={{ marginTop: 8 }} data-testid="gate-consent">
                We collect these details to prepare and deliver your report and to contact you about it.
                See our <a href="/privacy" style={{ color: "var(--cw-seafoam)" }}>privacy policy</a>.
              </p>

              <button
                type="submit"
                className="cw-btn cw-btn-primary"
                disabled={submitting}
                data-testid="gate-submit-btn"
                style={{ marginTop: 8, width: "100%" }}
              >
                {submitting ? "Sending\u2026" : "Send my verification code"}
              </button>
            </form>
          </div>
        )}

        {step === "verify" && (
          <div className="cw-fade-in">
            <button
              className="cw-btn-text"
              onClick={() => { setStep("gate"); setCodeError(null); }}
              data-testid="verify-back-btn"
              style={{ marginBottom: 24 }}
            >
              &larr; Back
            </button>
            <div className="cw-eyebrow" data-testid="verify-eyebrow">Step 02 &middot; Verify your email</div>
            <hr className="cw-rule" style={{ marginTop: 20 }} />
            <h1 className="cw-display-l" data-testid="verify-heading">Check your inbox.</h1>
            <p className="cw-body" style={{ marginTop: 16, color: "rgba(22,30,27,0.72)" }} data-testid="verify-body">
              We have sent a six-digit code to <strong style={{ color: "var(--cw-midnight)" }}>{form.work_email}</strong>.
              Enter it below to begin.
            </p>
            <div
              style={{
                marginTop: 20,
                padding: "14px 18px",
                border: "1px solid var(--cw-border-dark)",
                borderRadius: 14,
                background: "rgba(106,161,163,0.12)",
                borderColor: "rgba(106,161,163,0.35)",
                display: "flex",
                gap: 12,
                alignItems: "flex-start",
              }}
              data-testid="spam-hint"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden style={{ flexShrink: 0, marginTop: 2 }}>
                <circle cx="12" cy="12" r="9" stroke="var(--cw-seafoam)" strokeWidth="1.6"/>
                <path d="M12 8v5M12 16.5v.5" stroke="var(--cw-seafoam)" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
              <div className="cw-small" style={{ color: "rgba(22,30,27,0.82)", lineHeight: 1.55 }}>
                <strong style={{ color: "var(--cw-midnight)", fontWeight: 600 }}>Not in your inbox?</strong>
                {" "}Check your spam or junk folder. If you find our email there, open it and mark it as
                <em> Not spam</em> (Gmail) or <em>Not junk</em> (Outlook) so your report lands in your
                inbox when it arrives within the hour.
              </div>
            </div>
            {/* Dev-code banner intentionally disabled: verification codes must only be delivered by email. */}
            <div style={{ marginTop: 40 }}>
              <CodeEntry onComplete={submitCode} error={codeError} />
            </div>
            {codeError && (
              <p className="cw-small" style={{ color: "var(--cw-error)", marginTop: 12 }} data-testid="code-error">
                {codeError}
              </p>
            )}
            <p className="cw-small" style={{ marginTop: 24 }}>
              The code expires in 15 minutes.{" "}
              <button className="cw-btn-text" onClick={resend} disabled={resendCooldown > 0} data-testid="resend-code-btn">
                {resendCooldown > 0 ? `Resend in 0:${String(resendCooldown).padStart(2, "0")}` : "Resend code"}
              </button>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, name, placeholder, value, onChange, error, type = "text" }) {
  return (
    <div data-testid={`field-${name}`}>
      <div className="cw-label" style={{ marginBottom: 8 }}>{label}</div>
      <input
        type={type}
        className={`cw-input ${error ? "error" : ""}`}
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        data-testid={`input-${name}`}
      />
      {error && <div className="cw-small" style={{ color: "var(--cw-error)", marginTop: 6 }} data-testid={`error-${name}`}>{error}</div>}
    </div>
  );
}

function SelectField({ label, name, value, onChange, error, options, placeholder }) {
  const items = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  return (
    <div data-testid={`field-${name}`}>
      <div className="cw-label" style={{ marginBottom: 8 }}>{label}</div>
      <select
        className={`cw-select ${error ? "error" : ""}`}
        name={name}
        value={value}
        onChange={onChange}
        data-testid={`select-${name}`}
      >
        <option value="">{placeholder}</option>
        {items.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      {error && <div className="cw-small" style={{ color: "var(--cw-error)", marginTop: 6 }} data-testid={`error-${name}`}>{error}</div>}
    </div>
  );
}
